/**
 * Reading and writing form values - the primitives behind g.former.
 *
 * A "field" is any form control with a `name` (unnamed controls are UI-only
 * by definition and never touched; `former-ignore` opts a named one out).
 * Controls sharing a name form a group (checkbox lists, radios). Values are
 * normalized for the API:
 *   - selekt            -> getValue()  (scalar single / array multiple)
 *   - file-selekt       -> getValue()  (jsonb object single / array multiple)
 *   - date-selekt       -> the holder's PG wire string (ranges: the hidden
 *                          `<name>_to` input is a field of its own)
 *   - single checkbox   -> boolean
 *   - checkbox group    -> array of checked values
 *   - radio group       -> checked value or null
 *   - multi <select>    -> array of values
 *   - raw <input file>  -> File / File[] (g.fetch switches to multipart)
 *   - everything else   -> string
 * Disabled fields (including via a disabled <fieldset>) are skipped, exactly
 * like native form submission and the validator.
 */

const SKIP_TYPES = new Set(['submit', 'button', 'reset', 'image']);

// Named, collectible fields grouped by name (form order). Disabled fields
// (including via a disabled <fieldset>) are excluded like in a native submit;
// `withDisabled` includes them - fill() writes even into inactive sections,
// which onFill may enable right after.
export function fieldGroups(form, withDisabled = false) {
	const groups = new Map();
	for (const el of Array.from(form.elements)) {
		if (!el.name || SKIP_TYPES.has(el.type) || el.tagName === 'BUTTON' || el.tagName === 'FIELDSET') continue;
		if (el.hasAttribute('former-ignore') || el.hasAttribute('data-former-ignore')) continue;
		if (!withDisabled && el.matches(':disabled')) continue;
		const group = groups.get(el.name);
		if (group) group.push(el);
		else groups.set(el.name, [el]);
	}
	return groups;
}

export function readValue(group) {
	const el = group[0];
	if (el.selekt) return el.selekt.getValue();
	if (el.fileSelekt) return el.fileSelekt.getValue();
	if (el.type === 'checkbox') {
		if (group.length > 1) return group.filter((c) => c.checked).map((c) => c.value);
		return el.checked;
	}
	if (el.type === 'radio') {
		const checked = group.find((c) => c.checked);
		return checked ? checked.value : null;
	}
	if (el instanceof HTMLSelectElement && el.multiple) return Array.from(el.selectedOptions).map((o) => o.value);
	if (el.type === 'file') {
		const files = el.files ? Array.from(el.files) : [];
		return el.multiple ? files : files[0] || null;
	}
	return el.value;
}

// { name: value } for the whole form.
export function readValues(form, withDisabled = false) {
	const out = {};
	for (const [name, group] of fieldGroups(form, withDisabled)) out[name] = readValue(group);
	return out;
}

const truthy = (v) => v === true || v === 1 || v === '1' || v === 'true' || v === 'on';
const asList = (v) => (Array.isArray(v) ? v.map(String) : v == null || v === '' ? [] : [String(v)]);

// Write one value back into a field group (the reverse of readValue).
// date-selekt RANGES are handled by the caller (they span two fields).
export function applyValue(group, value) {
	const el = group[0];
	if (el.selekt) return el.selekt.setValue(value == null ? (el.multiple ? [] : '') : value);
	if (el.fileSelekt) return el.fileSelekt.setValue(value == null ? (el.fileSelekt.multiple ? [] : null) : value);
	if (el.dateSelekt) return el.dateSelekt.setValue(value == null ? null : value);
	if (el.editor) return el.editor.setValue(value == null ? '' : String(value));
	if (el.type === 'checkbox') {
		if (group.length > 1) {
			const list = asList(value);
			group.forEach((c) => { c.checked = list.includes(c.value); });
		} else el.checked = truthy(value);
		return;
	}
	if (el.type === 'radio') {
		group.forEach((c) => { c.checked = value != null && c.value === String(value); });
		return;
	}
	if (el instanceof HTMLSelectElement && el.multiple) {
		const list = asList(value);
		Array.from(el.options).forEach((o) => { o.selected = list.includes(o.value); });
		return;
	}
	if (el.type === 'file') { el.value = ''; return; } // raw pickers cannot be filled programmatically
	el.value = value == null ? '' : value;
}

// Stable JSON of a values object, used for dirty comparison. File objects
// (raw pickers) are reduced to a comparable identity.
export function serialize(values) {
	return JSON.stringify(values, (key, v) => (typeof File !== 'undefined' && v instanceof File ? { name: v.name, size: v.size, lastModified: v.lastModified } : v));
}
